import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { TableToolbar, FilterPills } from "../../shared/TableToolbar";
import PdfEstadoModalCxP from "../components/PdfEstadoModalCxP";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import { downloadExcel } from "../../shared/excelService";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { useTipoCambio } from "../../services/useTipoCambio";
import CurrencyToggle from "../../shared/CurrencyToggle";

// ── Agrupar CuentaXPagar por proveedor (equivalente a useCxcFiliales) ─────────
// Suma monto_colones/saldo_colones (equivalente en colones que ya mantiene el
// backend), no monto/saldo crudos — un proveedor con facturas CRC y USD
// mezcladas sumaría colones y dólares como si fueran la misma unidad.
function agruparPorProveedor(items, filtro, search, tcHoy) {
  // Equivalente en dólares de una fila, con el MISMO criterio que el estado de
  // cuenta PDF (ver to_display en core/api/views.py): una factura en USD vale
  // su monto nativo; una en colones se divide entre su propio tipo de cambio
  // —el del día en que se registró— y solo se recurre al TC de hoy si el
  // documento no trae tasa. Convertir el total en colones al TC de hoy daba
  // números distintos a los del PDF ($806.42 contra los $809.34 del estado).
  const aUsd = (item, campoNativo, campoColones) => {
    if ((item.moneda || "CRC").toUpperCase() === "USD") return Number(item[campoNativo] ?? 0);
    const tcDoc = Number(item.tipo_cambio ?? 0);
    const divisor = tcDoc > 1 ? tcDoc : tcHoy;
    const colones = Number(item[campoColones] ?? item[campoNativo] ?? 0);
    return divisor > 1 ? colones / divisor : colones;
  };

  const map = {};
  for (const item of items) {
    const fac    = item.factura_cxp;
    const key    = fac?.cuenta_proveedor?.id ?? "sin-proveedor";
    const nombre = fac?.cuenta_proveedor?.nombre ?? "Sin proveedor";
    if (!map[key]) {
      map[key] = {
        id:key, nombre, totalMonto:0, totalPagado:0, totalSaldo:0, nFacturas:0,
        totalMontoUsd:0, totalPagadoUsd:0, totalSaldoUsd:0,
      };
    }
    const montoColones = Number(item.monto_colones ?? item.monto ?? 0);
    const saldoColones = Number(item.saldo_colones ?? item.saldo ?? 0);
    map[key].totalMonto  += montoColones;
    map[key].totalPagado += montoColones - saldoColones;
    map[key].totalSaldo  += saldoColones;
    map[key].nFacturas++;

    const montoUsd = aUsd(item, "monto", "monto_colones");
    const saldoUsd = aUsd(item, "saldo", "saldo_colones");
    map[key].totalMontoUsd  += montoUsd;
    map[key].totalPagadoUsd += montoUsd - saldoUsd;
    map[key].totalSaldoUsd  += saldoUsd;
  }
  let filas = Object.values(map);

  if (filtro === "debiendo") filas = filas.filter(r => r.totalSaldo > 0);
  if (filtro === "aldia")    filas = filas.filter(r => r.totalSaldo <= 0);

  if (search.trim())
    filas = filas.filter(r => r.nombre.toLowerCase().includes(search.toLowerCase()));

  return filas.sort((a, b) => b.totalSaldo - a.totalSaldo);
}

// ── Card de resumen — theme-aware ─────────────────────────────────────────────
function SummaryCard({ label, value, icon, color, isCurrency = true, fmt }) {
  return (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border-card)",
      borderRadius: 10,
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: 14,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "1.1rem" }}/>
      </div>
      <div>
        <p style={{
          margin: 0, color: "var(--text-muted)", fontSize: "0.72rem",
          fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px",
        }}>
          {label}
        </p>
        <p style={{ margin: "2px 0 0", color: "var(--text-primary)", fontSize: "1.1rem", fontWeight: 700 }}>
          {isCurrency ? (fmt ? fmt(value) : `₡${formatCurrency(value)}`) : value}
        </p>
      </div>
    </div>
  );
}

// ── Fila — theme-aware ────────────────────────────────────────────────────────
function CxPRow({ row, t, onEstado, fmt }) {
  const saldo   = row.totalSaldo;
  const alDia   = saldo <= 0;
  const parcial = !alDia && row.totalPagado > 0;

  return (
    <tr>
      <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>{row.nombre}</td>

      <td>{row.nFacturas} {row.nFacturas !== 1 ? t("cxp.facturas") : t("cxp.facturas")}</td>

      <td>
        <div className="monto-typo" style={{ textAlign: "right", fontWeight: 600 }}>
          {fmt(row.totalMonto, row.totalMontoUsd)}
        </div>
      </td>

      <td>
        <div className="monto-typo" style={{
          textAlign: "right",
          color: row.totalPagado > 0 ? "#4ade80" : "var(--text-muted)",
        }}>
          {fmt(row.totalPagado, row.totalPagadoUsd)}
        </div>
      </td>

      <td>
        <div className="monto-typo" style={{
          textAlign: "right",
          fontWeight: 600,
          color: alDia ? "#4ade80" : "#f87171",
        }}>
          {fmt(Math.abs(saldo), Math.abs(row.totalSaldoUsd))}
          {saldo < 0 && (
            <span style={{ fontSize: "0.72rem", marginLeft: 4 }}>{t("cxp.aFavor")}</span>
          )}
        </div>
      </td>

      <td className="text-center">
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "3px 10px", borderRadius: 20,
          fontSize: "0.74rem", fontWeight: 600,
          background: alDia
            ? "rgba(74,222,128,0.1)"
            : parcial ? "rgba(251,191,36,0.1)" : "rgba(248,113,113,0.1)",
          color: alDia ? "#4ade80" : parcial ? "#fbbf24" : "#f87171",
          border: `1px solid ${alDia
            ? "rgba(74,222,128,0.25)"
            : parcial ? "rgba(251,191,36,0.25)" : "rgba(248,113,113,0.25)"}`,
        }}>
          <i className={`fa-solid ${alDia ? "fa-check" : parcial ? "fa-clock" : "fa-exclamation"}`}
             style={{ fontSize: "0.65rem" }}/>
          {alDia ? t("cxp.alDia") : parcial ? t("common.parcial") : t("common.pendiente")}
        </span>
      </td>

      <td className="text-center">
        {row.id !== "sin-proveedor" && (
          <button
            type="button"
            onClick={() => onEstado?.({ id: row.id, nombre: row.nombre })}
            title={t("cxp.estadoCuentaProveedor")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 10px", background: "transparent",
              border: "1px solid rgba(249,115,22,0.3)", borderRadius: 6,
              color: "#f97316", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = "rgba(249,115,22,0.15)"; e.currentTarget.style.borderColor = "#f97316"; }}
            onMouseOut={(e)  => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(249,115,22,0.3)"; }}
          >
            <i className="fa-solid fa-file-pdf" style={{ fontSize: "0.7rem" }} />
            {t("cxp.estadoPDF")}
          </button>
        )}
      </td>
    </tr>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ListCxP() {
  const { t } = useTranslation();
  const multimoneda = useMultimoneda();
  const { tipoCambio } = useTipoCambio();
  const tc = Number(tipoCambio?.tipo_compra) || 1;
  const [moneda, setMoneda] = useState("CRC");
  const esUSD = moneda === "USD";
  // El monto en dólares ya viene calculado por agruparPorProveedor(), que
  // valora cada factura con su propio tipo de cambio igual que el PDF.
  const fmtRow = (colones, dolares) => {
    if (!esUSD) return `₡${formatCurrency(Number(colones || 0))}`;
    const v = Number(dolares || 0);
    return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const [filtro,       setFiltro]       = useState("debiendo");
  const [search,       setSearch]       = useState("");
  const [page,         setPage]         = useState(1);
  const [allItems,     setAllItems]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [pdfModal,     setPdfModal]     = useState(null);

  const PAGE_SIZE = 12;

  // FILTROS built inside component so they re-render on language change
  const FILTROS = [
    { value: "debiendo", label: t("cxp.filtroConSaldo"), icon: "fa-exclamation-circle" },
    { value: "aldia",    label: t("cxp.filtroAlDia"),    icon: "fa-check-circle"       },
    { value: "todos",    label: t("cxp.filtroTodos"),    icon: "fa-list"               },
  ];

  const fetchData = useCallback(() => {
    setLoading(true);
    apiFetch("/api/finance/cuentaXPagar/?page_size=500")
      .then(r => r.json())
      .then(d => setAllItems(d.results ?? d ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filas = agruparPorProveedor(allItems, filtro, search, tc);

  const totalPages = Math.ceil(filas.length / PAGE_SIZE);
  const filasPage  = filas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleSearch(val) { setSearch(val); setPage(1); }
  function handleFiltro(val) { setFiltro(val); setPage(1); }

  const totalSaldo = filas.reduce((a, r) => a + r.totalSaldo, 0);
  const totalMonto = filas.reduce((a, r) => a + r.totalMonto, 0);
  // Mismos totales en dólares que las filas, para que las tarjetas de resumen
  // no muestren un número distinto al de la tabla.
  const totalSaldoUsd = filas.reduce((a, r) => a + r.totalSaldoUsd, 0);
  const totalMontoUsd = filas.reduce((a, r) => a + r.totalMontoUsd, 0);

  // ── Exportar Excel — hoja "Facturas por Proveedor" agrupada con subtotales
  // y semáforo Vencida/Vigente, generada en el backend (mismo layout para CxC).
  const [exportando, setExportando] = useState(false);
  async function exportarExcel() {
    setExportando(true);
    try {
      const fecha = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      await downloadExcel(
        `/api/finance/cxp/facturas-por-proveedor-excel/?fecha_corte=${fecha}`,
        `CxP_Facturas_Por_Proveedor_${fecha}.xlsx`
      );
    } catch (e) {
      alert(e.message || "Error al exportar el Excel");
    } finally {
      setExportando(false);
    }
  }

  return (
    <>
      {/* ── Breadcrumb ── */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("cxp.titulo")}</span>
      </span>

      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ marginBottom: 6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("cxp.gestion")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("cxp.administra")}</p>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
          {multimoneda && (
            <CurrencyToggle value={moneda} onChange={setMoneda} title="Moneda de visualización" />
          )}

          {/* Estado de Cuenta PDF */}
          <button
            onClick={() => setPdfModal({})}
            disabled={loading || filas.length === 0}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px",
              background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.35)",
              borderRadius: 8, color: "#f97316", fontSize: "0.83rem", fontWeight: 600,
              cursor: loading || filas.length === 0 ? "not-allowed" : "pointer",
              opacity: loading || filas.length === 0 ? 0.5 : 1, transition: "all 0.15s",
            }}
            onMouseOver={(e) => { if (!loading && filas.length > 0) e.currentTarget.style.background = "rgba(249,115,22,0.2)"; }}
            onMouseOut={(e) => { e.currentTarget.style.background = "rgba(249,115,22,0.1)"; }}
          >
            <i className="fa-solid fa-file-pdf" />
            {t("cxp.estadoCuentaProveedor")}
          </button>

          {/* Excel */}
          <button
            onClick={exportarExcel}
            disabled={loading || exportando || filas.length === 0}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px",
              background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.35)",
              borderRadius: 8, color: "#4ade80", fontSize: "0.83rem", fontWeight: 600,
              cursor: loading || exportando || filas.length === 0 ? "not-allowed" : "pointer",
              opacity: loading || exportando || filas.length === 0 ? 0.5 : 1, transition: "all 0.15s",
            }}
            onMouseOver={(e) => { if (!loading && !exportando && filas.length > 0) e.currentTarget.style.background = "rgba(74,222,128,0.2)"; }}
            onMouseOut={(e) => { e.currentTarget.style.background = "rgba(74,222,128,0.1)"; }}
          >
            <i className={`fa-solid ${exportando ? "fa-circle-notch fa-spin" : "fa-file-excel"}`} />
            {exportando ? "Generando..." : "Exportar Excel"}
          </button>
        </div>
      </div>

      {/* ── Cards resumen ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
        <SummaryCard
          label={t("cxp.proveedoresConSaldo")}
          value={filas.filter(r => r.totalSaldo > 0).length}
          icon="fa-building-circle-exclamation"
          color="#f97316"
          isCurrency={false}
        />
        <SummaryCard
          label={t("cxp.totalFacturado")}
          value={totalMonto}
          fmt={(v) => fmtRow(v, totalMontoUsd)}
          icon="fa-file-invoice-dollar"
          color="#60a5fa"
        />
        <SummaryCard
          label={t("cxp.totalPendiente")}
          value={totalSaldo}
          fmt={(v) => fmtRow(v, totalSaldoUsd)}
          icon="fa-money-bill-wave"
          color={totalSaldo > 0 ? "#f87171" : "#4ade80"}
        />
      </div>

      {/* ── Tabla principal ── */}
      <div className={invoiceStyles["table-container"]}>

        {/* Barra búsqueda + filtro estado */}
        <TableToolbar
          search={search}
          onSearch={handleSearch}
          searchPlaceholder={t("cxp.buscarPlaceholder")}
          loading={loading}
          extra={<FilterPills options={FILTROS} value={filtro} onChange={handleFiltro} />}
        />

        {/* Tabla */}
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("cxp.proveedor")}</th>
                <th>{t("sidebar.facturas")}</th>
                <th><div style={{ textAlign: "right" }}>{t("cxp.totalFacturadoCol")}</div></th>
                <th><div style={{ textAlign: "right" }}>{t("cxp.totalAbonadoCol")}</div></th>
                <th><div style={{ textAlign: "right" }}>{t("cxp.saldoAcumulado")}</div></th>
                <th>{t("common.estado")}</th>
                <th className="text-center">{t("cxp.reporte")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight: 8, color: "#f97316" }}/>
                  {t("cxp.cargando")}
                </td></tr>
              ) : filasPage.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox"
                     style={{ marginRight: 8, fontSize: "1.2rem" }}/>
                  {t("cxp.sinProveedores")}
                </td></tr>
              ) : (
                filasPage.map(row => <CxPRow key={row.id} row={row} t={t} onEstado={setPdfModal} fmt={fmtRow}/>)
              )}
            </tbody>
          </table>
        </div>

        {/* Footer paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }}/>
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{filas.length}</span> {t("cxp.proveedores")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {/* ── Modal PDF Estado de Cuenta Proveedor ── */}
      {pdfModal !== null && (
        <PdfEstadoModalCxP
          onClose={() => setPdfModal(null)}
          proveedorPreselected={pdfModal.id ? pdfModal : null}
        />
      )}
    </>
  );
}
